import { useEffect, useState } from "react";

import { type BaseRecord, useDataProvider } from "@refinedev/core";
import { z } from "zod";

import { parseRead } from "@/lib/zod/read-schema";

/**
 * One secondary read of a TINA-owned child document (standings, match details,
 * season stats). `empty` is a 404 or an empty body: "no data yet", not a fault.
 * `error` is anything else; callers show it inline and keep the page.
 */
export type SportsDetailState<T> =
  | { status: "loading" }
  | { status: "empty" }
  | { status: "error"; message: string }
  | { status: "ready"; data: T };

interface HttpErrorLike {
  statusCode?: number;
  status?: number;
  message?: unknown;
  response?: { status?: number; data?: unknown };
}

/**
 * Refine's axios instance rethrows `{ ...axiosError, message, statusCode }`, a plain
 * object rather than an `AxiosError`, so the status can sit on any of three fields.
 */
export function httpStatusOf(error: unknown): number | undefined {
  const e = error as HttpErrorLike | null | undefined;
  return e?.statusCode ?? e?.response?.status ?? e?.status;
}

export function isNotFound(error: unknown): boolean {
  return httpStatusOf(error) === 404;
}

/** A short human line for an inline error. Never throws on an odd error shape. */
export function describeFetchError(error: unknown): string {
  const e = error as HttpErrorLike | null | undefined;
  const status = httpStatusOf(error);
  const data = e?.response?.data as
    | { message?: unknown; detail?: unknown; title?: unknown }
    | undefined;
  const text = [data?.message, data?.detail, data?.title, e?.message].find(
    (v): v is string => typeof v === "string" && v.length > 0,
  );
  if (status && text) return `${status}: ${text}`;
  if (status) return `HTTP ${status}`;
  return text ?? "Network error";
}

/**
 * Fetches `GET {apiUrl}/{urlPath}` through the data provider's `custom` call and
 * validates it with a lenient view schema (raw fallback on a parse miss, as
 * `useShowRecord` does). Pass `undefined` while the parent record is loading.
 */
export function useSportsDetail<T>(
  urlPath: string | undefined,
  schema: z.ZodType<T>,
  label: string,
  query?: Record<string, string>,
): SportsDetailState<T> {
  const { custom } = useDataProvider()();
  const [state, setState] = useState<SportsDetailState<T>>({
    status: "loading",
  });
  const queryKey = JSON.stringify(query ?? {});

  useEffect(() => {
    if (!urlPath || !custom) return;
    let cancelled = false;
    setState({ status: "loading" });
    custom<BaseRecord>({ url: "", method: "get", meta: { urlPath }, query })
      .then((response) => {
        if (cancelled) return;
        const body: unknown = response?.data;
        setState(
          body === undefined || body === null || body === ""
            ? { status: "empty" }
            : { status: "ready", data: parseRead(schema, body, label) },
        );
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (isNotFound(error)) {
          setState({ status: "empty" });
          return;
        }
        console.error(`Failed to load ${label}`, error);
        setState({ status: "error", message: describeFetchError(error) });
      });
    return () => {
      cancelled = true;
    };
  }, [urlPath, queryKey]);

  return state;
}
